/*
 * pt26-collection-grid.js — <pt26-collection-grid>, das Produktgrid der
 * Collection Page mit Endless Scroll.
 *
 * Lädt die Folgeseite über die Section Rendering API nach, sobald der Sentinel
 * am Listenende in Reichweite kommt. Ohne JavaScript bleibt die nummerierte
 * Pagination aus dem Liquid stehen. Lazy geladen von pt26-core.js als
 * assets/pt26-collection-grid.js; beim Import läuft keine Arbeit.
 */

// Statt statischer Imports: ein relativer Import ohne ?v= laedt pt26-core.js bzw. pt26-vendor-embla.js
// ein zweites Mal als eigenes Modul. Die versionierte URL kommt aus #pt26-module-urls (layout/theme.liquid).
const pt26ModuleUrls = JSON.parse(document.getElementById('pt26-module-urls')?.textContent || '{}');
const { PT26_EVENTS } = await import(pt26ModuleUrls['pt26-core.js'] || './pt26-core.js');

// Promotion-Kacheln (snippets/pt26-promotion-tile.liquid). Liegt in dieser Datei, weil ein
// relativer Import ohne ?v= vom CDN veraltet ausgeliefert wird. Zufallslogik 1:1 aus assets/theme.js:8437–8550,
// die Regel für die erste Kachel weicht bewusst ab (Telefonat Felix, 06.10.2026).

const TILE_SELECTOR = ':scope > [data-pt26-promotion]';
const ITEM_SELECTOR = '[data-pt26-collection-grid-item]';
// Spaltenzahl mobil aus sections/pt26-collection-grid.liquid. Bis hierhin gilt die Mobil-Regel für die erste Kachel.
const MOBILE_COLUMN_COUNT = 2;
// Mindestabstand in Produktreihen zwischen zwei Promotion-Kacheln. Kleiner wäre dieselbe oder eine angrenzende Reihe.
const MIN_TILE_ROW_DISTANCE = 2;

/**
 * Spaltenzahl des aktuellen Breakpoints aus --pt26-collection-grid-columns
 * (sections/pt26-collection-grid.liquid).
 *
 * Nicht aus gridTemplateColumns: vor dem Layout steht dort die Formel, und
 * implizite Spuren einer alten Platzierung zählen mit, das hält eine falsche
 * Spaltenzahl nach einem Breakpoint-Wechsel fest.
 *
 * @param {HTMLElement} list - Das Grid ([data-pt26-collection-grid-list]).
 * @returns {number}
 */
function readColumnCount(list) {
  const columnCount = parseInt(getComputedStyle(list).getPropertyValue('--pt26-collection-grid-columns'), 10);
  return columnCount > 0 ? columnCount : 1;
}

/**
 * Spuren pro Produktreihe aus --pt26-collection-grid-row-tracks
 * (sections/pt26-collection-grid.liquid). Mit subgrid sind es vier (Bild,
 * Titel, Caption, Preis), ohne subgrid fehlt die Property und es bleibt eine.
 *
 * @param {HTMLElement} list - Das Grid ([data-pt26-collection-grid-list]).
 * @returns {number}
 */
function readRowTracks(list) {
  const rowTracks = parseInt(getComputedStyle(list).getPropertyValue('--pt26-collection-grid-row-tracks'), 10);
  return rowTracks > 0 ? rowTracks : 1;
}

/**
 * @param {HTMLElement} tile
 * @returns {number}
 */
function readTileWidth(tile) {
  return parseInt(tile.getAttribute('data-pt26-promotion-width'), 10) || 1;
}

/**
 * @param {HTMLElement} tile - Eine platzierte Kachel.
 * @param {number} rowTracks - Spuren pro Produktreihe.
 * @returns {number} Produktreihe ab 1, 0 ohne Platzierung.
 */
function readTileRow(tile, rowTracks) {
  const track = parseInt(tile.style.gridRow, 10);
  return track > 0 ? Math.round((track - 1) / rowTracks) + 1 : 0;
}

/**
 * @param {HTMLElement} tile
 * @returns {boolean} Ob das CSS die Kachel platziert (erste Kachel, data-pt26-promotion-first).
 */
function isStaticTile(tile) {
  return tile.hasAttribute('data-pt26-promotion-first');
}

/**
 * @param {HTMLElement} tile
 */
function resetTile(tile) {
  // Die erste Kachel kommt sichtbar aus dem Liquid, das CSS platziert sie (sections/pt26-collection-grid.liquid).
  if (isStaticTile(tile)) {
    return;
  }

  tile.hidden = true;
  tile.style.gridRow = '';
  tile.style.gridColumn = '';
}

/**
 * Bei zwei Spalten ist nur die erste zufällig, danach wird gewechselt, damit
 * die Kacheln nicht alle auf derselben Seite stehen.
 *
 * @param {number} columnCount
 * @param {number} tileCount
 * @returns {number[]} Spalten ab 1.
 */
function pickColumns(columnCount, tileCount) {
  const columns = [];

  if (columnCount === 2) {
    let column = Math.floor(Math.random() * 2) + 1;

    for (let i = 0; i < tileCount; i++) {
      columns.push(column);
      column = column === 1 ? 2 : 1;
    }

    return columns;
  }

  for (let i = 0; i < tileCount; i++) {
    columns.push(Math.floor(Math.random() * columnCount) + 1);
  }

  return columns;
}

/**
 * Setzt Reihe und Spalte der Promotion-Kacheln und blendet sie ein. Ab drei Spalten steht die erste Kachel
 * rechtsbündig in Reihe 1, mobil in Reihe 2, weitere verteilen sich dahinter ohne die letzte Reihe.
 *
 * @param {HTMLElement} list - Das Grid ([data-pt26-collection-grid-list]).
 * @param {boolean} [onlyHiddenTiles=false] - Nach dem Nachladen: sichtbare
 *   Kacheln behalten ihren Platz, zählen aber in der Rechnung mit.
 */
function placePromotionTiles(list, onlyHiddenTiles = false) {
  const tiles = Array.from(list.querySelectorAll(TILE_SELECTOR));

  if (tiles.length === 0) {
    return;
  }

  if (!onlyHiddenTiles) {
    tiles.forEach(resetTile);
  }

  const columnCount = readColumnCount(list);
  const productCount = list.querySelectorAll(ITEM_SELECTOR).length;
  const totalTileWidth = tiles.reduce((sum, tile) => sum + readTileWidth(tile), 0);

  const rowCount = Math.ceil(productCount / columnCount);
  const rowCountTotal = Math.ceil((productCount + totalTileWidth) / columnCount);

  // Mobil braucht das Grid erst eine volle Produktreihe, bevor eine Kachel kommt (Telefonat Felix, 06.10.2026).
  const firstRow = columnCount > MOBILE_COLUMN_COUNT ? 1 : 2;
  const firstTileWidth = Math.min(readTileWidth(tiles[0]), columnCount);

  // Produkte vor und neben der ersten Kachel. Fehlen sie, entstünde eine Lücke im Grid.
  const minProductCount = (firstRow - 1) * columnCount + (columnCount - firstTileWidth);

  if (totalTileWidth <= 0 || productCount < minProductCount) {
    // Fehlen die Produkte neben der festen ersten Kachel, würde sie eine Lücke reißen.
    if (isStaticTile(tiles[0])) {
      tiles[0].hidden = true;
    }

    return;
  }

  if (isStaticTile(tiles[0])) {
    tiles[0].hidden = false;
  }

  // Von der Reihe der ersten Kachel bis vor die letzte Reihe.
  const availableRowCount = Math.max(rowCountTotal - firstRow, 1);

  // Höchstens eine Kachel pro zwei Produktreihen, kaufmännisch gerundet (3 Reihen → 2 Kacheln in Reihe 1 und 3), damit
  // zwischen den Kacheln Abstand bleibt. Die erste steht immer. Die letzte Reihe bleibt frei (lastAllowedRow unten).
  const maxTileCount = Math.max(Math.round(rowCount / 2), 1);
  const distributedTileCount = Math.min(totalTileWidth, maxTileCount);
  const rowStep = Math.floor(availableRowCount / distributedTileCount);
  let rowRemainder = availableRowCount % distributedTileCount;

  const rows = [firstRow];
  let lastRow = firstRow;

  for (let i = 1; i < Math.min(tiles.length, maxTileCount); i++) {
    let extraRow = 0;

    if (distributedTileCount - i <= rowRemainder && rowRemainder > 0) {
      extraRow = 1;
      rowRemainder--;
    }

    const row = rowStep + lastRow + extraRow;
    lastRow = row;
    rows.push(row);
  }

  const rowTracks = readRowTracks(list);

  // Die Verteilung rechnet mit jeder Seite neu. Sichtbare Kacheln behalten deshalb ihre Reihe, neue stehen
  // mindestens zwei Reihen dahinter und nie in der letzten Reihe.
  const lastAllowedRow = Math.max(rowCountTotal - 1, firstRow);
  const placedRows = [];

  for (let i = 0; i < rows.length; i++) {
    // Die feste erste Kachel steht per CSS in firstRow und zählt für den Abstand der übrigen mit.
    if (i === 0 && isStaticTile(tiles[0])) {
      placedRows.push(firstRow);
      continue;
    }

    if (onlyHiddenTiles && !tiles[i].hidden) {
      placedRows.push(readTileRow(tiles[i], rowTracks));
      continue;
    }

    const previousRow = placedRows.length > 0 ? placedRows[placedRows.length - 1] : 0;
    const row = i === 0 ? rows[i] : Math.max(rows[i], previousRow + MIN_TILE_ROW_DISTANCE);

    if (i > 0 && row > lastAllowedRow) {
      break;
    }

    placedRows.push(row);
  }

  const columns = pickColumns(columnCount, placedRows.length);

  // In Reihe 1 steht die erste Kachel rechts, damit links davon zuerst Produkte kommen (die feste Kachel macht das per CSS).
  if (firstRow === 1) {
    columns[0] = columnCount - firstTileWidth + 1;
  }

  placedRows.forEach((row, index) => {
    const tile = tiles[index];

    if (isStaticTile(tile) || (onlyHiddenTiles && !tile.hidden)) {
      return;
    }

    const tileWidth = Math.min(readTileWidth(tile), columnCount);
    // Eine Spalte hinter dem Grid legt eine implizite Spur an und drückt die echten Spalten auf null.
    let column = Math.min(columns[index], columnCount);

    // Eine Doppelkachel in der letzten Spalte rückt eine nach links, statt aus dem Grid zu laufen.
    if (tileWidth + column > columnCount + 1) {
      column--;
    }

    // Produktreihe in Grid-Spuren umrechnen, die Kachel überspannt alle Spuren ihrer Reihe.
    tile.style.gridRow = (row - 1) * rowTracks + 1 + ' / span ' + rowTracks;
    tile.style.gridColumn = column + ' / ' + (column + tileWidth);
    tile.hidden = false;
  });
}

// Vorlauf: die Folgeseite kommt, bevor das Listenende sichtbar ist. Das Altsystem (theme.js:11399)
// nimmt 600px, das sind mobil nur gut eine Reihe, deshalb hier mehr.
const SENTINEL_ROOT_MARGIN = '1200px 0px';
const REQUEST_TIMEOUT_MS = 12000;

class Pt26CollectionGrid extends HTMLElement {
  constructor() {
    super();

    // Bewusst kein Zustand aus Attributen hier: ein Filterwechsel ersetzt das
    // Markup, und nur connectedCallback sieht dann die neuen Werte.
    this.list = null;
    this.sentinel = null;
    this.status = null;
    this.spinner = null;
    this.pagination = null;
    this.observer = null;
    this.abortController = null;
    this.timeoutHandle = null;
    this.isLoading = false;
    this.resizeObserver = null;
    this.columnCount = 0;

    this.handleIntersection = this.handleIntersection.bind(this);
    this.handlePageAppended = this.handlePageAppended.bind(this);
    this.handleListResize = this.handleListResize.bind(this);
  }

  connectedCallback() {
    this.list = this.querySelector('[data-pt26-collection-grid-list]');
    this.sentinel = this.querySelector('[data-pt26-collection-grid-sentinel]');
    this.status = this.querySelector('[data-pt26-collection-grid-status]');
    this.spinner = this.querySelector('[data-pt26-collection-grid-spinner]');
    this.pagination = this.querySelector('[data-pt26-collection-grid-pagination]');

    this.initPromotionTiles();

    // Ohne Observer bleibt die Pagination die Navigation.
    if (!this.list || !this.sentinel || !('IntersectionObserver' in window)) {
      return;
    }

    if (this.pagination) {
      this.pagination.hidden = true;
    }

    if (!this.getNextUrl()) {
      this.sentinel.hidden = true;
      return;
    }

    this.sentinel.hidden = false;
    this.observer = new IntersectionObserver(this.handleIntersection, {
      rootMargin: SENTINEL_ROOT_MARGIN,
    });
    this.observer.observe(this.sentinel);
  }

  disconnectedCallback() {
    this.stopObserving();
    this.cancelRequest();
    this.teardownPromotionTiles();
  }

  /**
   * Promotion-Kacheln platzieren und bei Nachladen oder Spaltenwechsel
   * nachführen (placePromotionTiles oben in dieser Datei).
   */
  initPromotionTiles() {
    if (!this.list) {
      return;
    }

    this.columnCount = readColumnCount(this.list);
    placePromotionTiles(this.list);

    this.addEventListener(PT26_EVENTS.collectionPageAppended, this.handlePageAppended);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.handleListResize);
      this.resizeObserver.observe(this.list);
    }
  }

  teardownPromotionTiles() {
    this.removeEventListener(PT26_EVENTS.collectionPageAppended, this.handlePageAppended);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  handlePageAppended() {
    placePromotionTiles(this.list, true);
  }

  /**
   * Nur ein Wechsel der Spaltenzahl platziert neu. Sonst könnte eine
   * Doppelkachel nach einem Breakpoint aus dem Grid laufen.
   */
  handleListResize() {
    const columnCount = readColumnCount(this.list);

    if (columnCount === this.columnCount) {
      return;
    }

    this.columnCount = columnCount;
    placePromotionTiles(this.list);
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  handleIntersection(entries) {
    if (entries.some((entry) => entry.isIntersecting)) {
      this.loadNextPage();
    }
  }

  /**
   * Seitenzahl und Folge-URL stehen als data-Attribut am Element, damit ein
   * neu verbundenes Element immer mit dem Stand weitermacht, den das Markup trägt.
   *
   * @returns {string}
   */
  getNextUrl() {
    return this.readPageUrl(this.getAttribute('data-next-url'));
  }

  /**
   * @returns {number}
   */
  getCurrentPage() {
    return Number(this.getAttribute('data-current-page')) || 1;
  }

  /**
   * Seiten-URL aus dem Liquid (mit Filtern und sort_by) plus section_id, damit
   * Shopify nur diese Section rendert.
   *
   * @param {string} pageUrl
   * @returns {string}
   */
  buildRequestUrl(pageUrl) {
    const requestUrl = new URL(pageUrl, window.location.origin);
    requestUrl.searchParams.set('section_id', this.getAttribute('data-section-id'));
    return requestUrl.toString();
  }

  async loadNextPage() {
    const pageUrl = this.getNextUrl();

    // Die Sperre verhindert, dass ein zweiter Treffer des Observers dieselbe Seite doppelt anhängt.
    if (this.isLoading || !pageUrl) {
      return;
    }

    const controller = new AbortController();
    this.abortController = controller;
    this.isLoading = true;
    this.setStatus(this.getAttribute('data-loading-text') || '');

    // Der Timeout bricht den Request wirklich ab, statt nur still auszulaufen.
    this.timeoutHandle = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(this.buildRequestUrl(pageUrl), {
        credentials: 'same-origin',
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error('HTTP ' + response.status);
      }

      const html = await response.text();

      // Inzwischen getrennt oder ersetzt: die Antwort gehört nicht mehr zu diesem Grid.
      if (this.abortController !== controller) {
        return;
      }

      this.appendPage(html, pageUrl);
    } catch (error) {
      if (this.abortController !== controller) {
        return;
      }

      console.error('[pt26] collection-grid: next page failed to load', error);
      this.showPagination();
    } finally {
      if (this.abortController === controller) {
        window.clearTimeout(this.timeoutHandle);
        this.timeoutHandle = null;
        this.abortController = null;
        this.isLoading = false;
        this.setStatus('');
        this.recheckSentinel();
      }
    }
  }

  /**
   * Übernimmt die Kacheln der Antwort und deren Folge-URL, dann das Ereignis.
   *
   * @param {string} html - Antwort der Section Rendering API.
   * @param {string} pageUrl - URL der angehängten Seite, ohne section_id.
   */
  appendPage(html, pageUrl) {
    const responseDocument = new DOMParser().parseFromString(html, 'text/html');
    const responseGrid = responseDocument.querySelector('pt26-collection-grid');

    if (!responseGrid) {
      throw new Error('response contains no pt26-collection-grid');
    }

    const page = Number(responseGrid.getAttribute('data-current-page'));

    const isNewPage = page > this.getCurrentPage();

    // Eine Seite, die schon da ist, wird nie ein zweites Mal angehängt.
    if (isNewPage) {
      const fragment = document.createDocumentFragment();
      responseGrid
        .querySelectorAll('[data-pt26-collection-grid-list] [data-pt26-collection-grid-item]')
        .forEach((item) => fragment.appendChild(document.importNode(item, true)));

      this.list.appendChild(fragment);
      this.setAttribute('data-current-page', String(page));
    }

    const nextUrl = this.readPageUrl(responseGrid.getAttribute('data-next-url'));

    if (isNewPage && nextUrl) {
      this.setAttribute('data-next-url', nextUrl);
    } else {
      this.removeAttribute('data-next-url');
      this.stopObserving();
      this.sentinel.hidden = true;
    }

    if (isNewPage) {
      this.dispatchEvent(
        new CustomEvent(PT26_EVENTS.collectionPageAppended, {
          bubbles: true,
          detail: { page, url: pageUrl },
        })
      );
    }
  }

  /**
   * Die Folge-URL aus einer Section-Antwort kann section_id tragen. Die gehört
   * nicht in die Seiten-URL, die das Element führt.
   *
   * @param {string | null} url
   * @returns {string}
   */
  readPageUrl(url) {
    if (!url) {
      return '';
    }

    const pageUrl = new URL(url, window.location.origin);
    pageUrl.searchParams.delete('section_id');
    return pageUrl.pathname + pageUrl.search;
  }

  /**
   * Steht der Sentinel nach dem Anhängen noch in Reichweite, meldet der
   * Observer das nicht von selbst. Neu beobachten liefert den aktuellen Stand.
   */
  recheckSentinel() {
    if (!this.observer || !this.getNextUrl()) {
      return;
    }

    this.observer.unobserve(this.sentinel);
    this.observer.observe(this.sentinel);
  }

  showPagination() {
    this.stopObserving();
    this.sentinel.hidden = true;

    if (this.pagination) {
      this.pagination.hidden = false;
    }
  }

  /**
   * Der Spinner steht nur, solange ein Ladetext da ist.
   *
   * @param {string} text
   */
  setStatus(text) {
    if (this.status) {
      this.status.textContent = text;
    }

    if (this.spinner) {
      this.spinner.hidden = text === '';
    }
  }

  stopObserving() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  cancelRequest() {
    window.clearTimeout(this.timeoutHandle);
    this.timeoutHandle = null;

    const controller = this.abortController;
    this.abortController = null;
    this.isLoading = false;

    if (controller) {
      controller.abort();
    }
  }
}

if (!customElements.get('pt26-collection-grid')) {
  customElements.define('pt26-collection-grid', Pt26CollectionGrid);
}
